const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KEYS = {
  TOKEN: 'rubicon_admin_token',
  USER: 'rubicon_admin_user',
  // legacy localStorage data keys (used only for one-time import)
  PROPOSALS: 'rubicon_proposals',
  RISK_PROFILES: 'rubicon_risk_profiles',
  ONBOARDINGS: 'rubicon_onboardings'
};

export const formatApiError = (detail, fallback = 'Something went wrong. Please try again.') => {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  return detail.msg || String(detail);
};

const request = async (path, { method = 'GET', body, auth = true } = {}) => {
  const headers = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem(KEYS.TOKEN);
  if (auth && token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API}${path}`, { method, headers, body: body !== undefined ? JSON.stringify(body) : undefined });
  if (res.status === 401 && auth) {
    logout();
    if (!window.location.pathname.startsWith('/admin/login')) window.location.assign('/admin/login');
  }
  const data = res.status === 204 ? null : await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error(formatApiError(data?.detail));
    err.status = res.status;
    throw err;
  }
  return data;
};

// ---- Auth ----
export const login = async (username, password) => {
  try {
    const data = await request('/auth/login', { method: 'POST', body: { username, password }, auth: false });
    localStorage.setItem(KEYS.TOKEN, data.token);
    localStorage.setItem(KEYS.USER, JSON.stringify(data.user));
    return { success: true, user: data.user };
  } catch (err) {
    return { success: false, message: err.message };
  }
};

export const logout = () => {
  localStorage.removeItem(KEYS.TOKEN);
  localStorage.removeItem(KEYS.USER);
};

export const isAuthenticated = () => !!localStorage.getItem(KEYS.TOKEN);

export const getCurrentUser = () => {
  const raw = localStorage.getItem(KEYS.USER);
  try { return raw ? JSON.parse(raw) : null; } catch { return null; }
};

export const verifySession = async () => {
  const user = await request('/auth/me');
  localStorage.setItem(KEYS.USER, JSON.stringify(user));
  return user;
};

// ---- Proposals ----
export const getAllProposals = () => request('/proposals');
export const getProposal = (id) => request(`/proposals/${id}`);
export const createProposal = (data) => request('/proposals', { method: 'POST', body: data });
export const updateProposal = (id, updates) => request(`/proposals/${id}`, { method: 'PUT', body: updates });
export const deleteProposal = (id) => request(`/proposals/${id}`, { method: 'DELETE' });
export const updateProposalStatus = (id, status) => updateProposal(id, { status });
export const getProposalStats = () => request('/proposals-stats');

// ---- Users ----
export const getAllUsers = () => request('/users');
export const createUser = (data) => request('/users', { method: 'POST', body: data });
export const deleteUser = (id) => request(`/users/${id}`, { method: 'DELETE' });

// ---- Risk Profiles ----
export const saveRiskProfile = async (data) => (await request('/risk-profiles', { method: 'POST', body: data })).id;
export const getRiskProfiles = () => request('/risk-profiles');
export const getRiskProfile = (id) => request(`/risk-profiles/${id}`);
export const deleteRiskProfile = (id) => request(`/risk-profiles/${id}`, { method: 'DELETE' });

// ---- Onboardings ----
export const saveOnboarding = async (data) => (await request('/onboardings', { method: 'POST', body: data })).id;
export const getOnboardings = () => request('/onboardings');
export const getOnboarding = (id) => request(`/onboardings/${id}`);
export const deleteOnboarding = (id) => request(`/onboardings/${id}`, { method: 'DELETE' });

// ---- Blog (public read, admin write) ----
export const getPublicBlogs = () => request('/blogs', { auth: false });
export const getPublicBlog = (slug) => request(`/blogs/${slug}`, { auth: false });
export const getAdminBlogs = () => request('/admin/blogs');
export const getAdminBlog = (id) => request(`/admin/blogs/${id}`);
export const createBlog = (data) => request('/blogs', { method: 'POST', body: data });
export const updateBlog = (id, data) => request(`/blogs/${id}`, { method: 'PUT', body: data });
export const deleteBlog = (id) => request(`/blogs/${id}`, { method: 'DELETE' });

// ---- Shares (client self-fill links) ----
export const createShare = (kind) => request('/shares', { method: 'POST', body: { kind } });
export const getPublicShare = (token) => request(`/public/shares/${token}`, { auth: false });
export const submitPublicShare = (token, data) => request(`/public/shares/${token}`, { method: 'POST', body: data, auth: false });

// ---- File uploads (Emergent Object Storage) ----
export const uploadFile = async (file, publicToken = null) => {
  const form = new FormData();
  form.append('file', file);
  const path = publicToken ? `/public/shares/${publicToken}/upload` : '/upload';
  const headers = {};
  const token = localStorage.getItem(KEYS.TOKEN);
  if (!publicToken && token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API}${path}`, { method: 'POST', body: form, headers });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(formatApiError(data?.detail));
  return { ...data, absoluteUrl: `${process.env.REACT_APP_BACKEND_URL}${data.url}` };
};

// Blog image upload (admin only; stored public so blog readers can view)
export const uploadBlogImage = async (file) => {
  const form = new FormData();
  form.append('file', file);
  const token = localStorage.getItem(KEYS.TOKEN);
  const res = await fetch(`${API}/blogs/upload-image`, {
    method: 'POST',
    body: form,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(formatApiError(data?.detail));
  return { ...data, absoluteUrl: `${process.env.REACT_APP_BACKEND_URL}${data.url}` };
};

// Append the correct access credential to a stored file URL (auth token for team, share token for public client).
export const fileUrl = (url, publicToken = null) => {
  if (!url) return url;
  const sep = url.includes('?') ? '&' : '?';
  if (publicToken) return `${url}${sep}share=${encodeURIComponent(publicToken)}`;
  const token = localStorage.getItem(KEYS.TOKEN);
  return token ? `${url}${sep}auth=${encodeURIComponent(token)}` : url;
};

// ---- DPDP data rights requests ----
export const submitDataRequest = (payload) => request('/data-requests', { method: 'POST', body: payload });
export const getDataRequests = () => request('/data-requests');
export const updateDataRequest = (id, body) => request(`/data-requests/${id}`, { method: 'PUT', body });
export const deleteDataRequest = (id) => request(`/data-requests/${id}`, { method: 'DELETE' });

// ---- Password management ----
export const changePassword = (current_password, new_password) => request('/auth/change-password', { method: 'POST', body: { current_password, new_password } });
export const resetUserPassword = (userId, new_password) => request(`/users/${userId}/reset-password`, { method: 'POST', body: { new_password } });

// ---- One-time import of legacy browser data ----
const readLegacy = (key) => {
  try { const v = JSON.parse(localStorage.getItem(key) || '[]'); return Array.isArray(v) ? v : []; } catch { return []; }
};

export const getLegacyDataCounts = () => ({
  proposals: readLegacy(KEYS.PROPOSALS).length,
  risk_profiles: readLegacy(KEYS.RISK_PROFILES).length,
  onboardings: readLegacy(KEYS.ONBOARDINGS).length
});

export const importLegacyData = async () => {
  const result = await request('/import', {
    method: 'POST',
    body: {
      proposals: readLegacy(KEYS.PROPOSALS),
      risk_profiles: readLegacy(KEYS.RISK_PROFILES),
      onboardings: readLegacy(KEYS.ONBOARDINGS)
    }
  });
  [KEYS.PROPOSALS, KEYS.RISK_PROFILES, KEYS.ONBOARDINGS].forEach(k => localStorage.removeItem(k));
  return result;
};
